<template>
  <div>
    <asc-tab-body :value="currentTabIndex">
      <asc-tab-item>
        <asc-report-details-wrapper
          :is-apu-report="isApuReport"
          :items="reportData"
          :loading="reportDataLoading"
          :on-report-menu-click="onReportMenuClick"
          :aircraft-details="aircraftDetails"
          :aircraft-details-loading="aircraftDetailsLoading"
          :export-button-visible="exportButtonVisible"
          :on-export-report="onExportReport"
          :report-alerts="reportAlerts"
          :on-report-alert-click="onReportAlertClick"
          :fault-monitors="faultMonitors"
          :on-show-raw-report="onShowRawReport"
        ></asc-report-details-wrapper>
      </asc-tab-item>
      <asc-tab-item>
        <asc-spinner-wrapper :loading="reportDataLoading">
          <asc-report-plots-wrapper
            :page-plots="pagePlots"
            :loading-plot-ids="loadingPlotIds"
            :loading-page-plots="loadingPagePlots"
            :chart-data="chartData"
            :internal-user="internalUser"
            :editable="editable"
            :plot-editor="plotEditor"
            :saving="saving"
            :disabled="disabled"
            :graph-groups="graphGroups"
            :on-toggle-page-plot-visibility="onTogglePagePlotVisibility"
            :on-expand="onExpand"
            :on-add-tab-to-page-plot="onAddTabToPagePlot"
            :on-remove-tab-from-page-plot="onRemoveTabFromPagePlot"
            :on-update-tab-view-page-plot="onUpdateTabViewPagePlot"
            :date-range="plotGroupDateRange"
            :graph-group-selected="graphGroupSelected"
            @graphGroupChange="handleUpdateGraphGroup"
            @onDateChange="handleUpdateDateRange"
          ></asc-report-plots-wrapper>
        </asc-spinner-wrapper>
      </asc-tab-item>
    </asc-tab-body>
  </div>
</template>

<script>
import AscTabBody from '@/components/atoms/AscTabBody'
import AscTabItem from '@/components/atoms/AscTabItem'
import AscReportPlotsWrapper from '@/components/organisms/AscReportPlotsWrapper'
import AscSpinnerWrapper from '@/components/atoms/AscSpinnerWrapper'
import { AscReportDetailsWrapper } from '@/components/organisms'
import {
  AscReportAlertMixin,
  AscReportMenuClickMixin,
} from '@/components/mixins'

export default {
  name: 'AscReportDetailsTemplate',

  components: {
    AscReportDetailsWrapper,
    AscTabBody,
    AscTabItem,
    AscReportPlotsWrapper,
    AscSpinnerWrapper,
  },

  mixins: [AscReportAlertMixin, AscReportMenuClickMixin],

  props: {
    tabIndex: {
      type: Number,
      default: 0,
    },
    reportId: {
      type: Number,
      required: true,
    },
    isApuReport: {
      type: Boolean,
      default: false,
    },
    reportData: {
      type: Array,
      required: true,
    },
    reportAlerts: {
      type: Array,
      required: true,
    },
    aircraftDetails: {
      type: Array,
      required: true,
    },
    faultMonitors: {
      type: Array,
      required: true,
    },
    reportDataLoading: {
      type: Boolean,
      default: false,
    },
    aircraftDetailsLoading: {
      type: Boolean,
      default: false,
    },
    availablePlots: {
      type: Boolean,
      default: false,
    },
    exportButtonVisible: {
      type: Boolean,
      default: false,
    },
    onExportReport: {
      type: Function,
      required: true,
    },
    onShowRawReport: {
      type: Function,
      required: true,
    },
    pagePlots: {
      type: Array,
      default: () => [],
    },
    chartData: {
      type: Array,
      default: () => [],
    },
    loadingPlotIds: {
      type: Array,
      default: () => [],
    },
    internalUser: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    plotEditor: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    visiblePlots: {
      type: Array,
      default: () => [],
    },
    onDeletePagePlot: {
      type: Function,
      default: () => {},
    },
    onTogglePagePlotVisibility: {
      type: Function,
      default: () => {},
    },
    onExpand: {
      type: Function,
      default: () => {},
    },
    onAddTabToPagePlot: {
      type: Function,
      default: () => {},
    },
    onRemoveTabFromPagePlot: {
      type: Function,
      default: () => {},
    },
    onUpdateTabViewPagePlot: {
      type: Function,
      default: () => {},
    },
    loadingPagePlots: {
      type: Boolean,
      default: false,
    },
    graphGroups: {
      type: Array,
      default: () => [],
    },
    plotGroupDateRange: {
      type: Number,
      default: 1,
    },
    graphGroupSelected: {
      type: Number,
      default: null,
    },
    onReportAlertClick: {
      type: Function,
      default: () => {},
    },
  },

  data() {
    return {
      currentTabIndex: this.tabIndex,
    }
  },

  watch: {
    pagePlots: {
      deep: true,
      handler(pagePlots) {
        this.pagePlots = pagePlots
      },
    },
    tabIndex(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.currentTabIndex = newVal
      }
    },
  },

  methods: {
    handleUpdateDateRange(data) {
      this.$emit('onDateChange', data)
    },

    handleUpdateGraphGroup(data) {
      this.$emit('graphGroupChange', data)
    },
  },
}
</script>

<style lang="scss">
.asc-report-details-template {
  @media only screen and (min-width: 900px) {
    .asc-report-details-template--tab-details {
      padding: 0 !important;
    }
  }

  &--container {
    margin-bottom: 16px;
  }

  &--container-full {
    background-color: $primary-white;
    overflow-x: auto;
    // prettier-ignore
    box-shadow:
      0 2px 1px -1px rgba(0, 0, 0, 0.2),
      0 1px 1px 0 rgba(0, 0, 0, 0.14),
      0 1px 3px 0 rgba(0, 0, 0, 0.12);
  }

  .asc-report-details-template--plot-details,
  .asc-report-details-template--report-details {
    padding: 0;
    background-color: $primary-white;
  }
}
</style>
